import React, { useCallback, useEffect, useState, createContext, useContext, createElement } from 'react';
// --- IDIOMAS SOPORTADOS -------------------------------------------------
// value: código que usa Google Translate (el select oculto .goog-te-combo)
// nativeName: nombre del idioma escrito en su propio idioma (no solo banderas)
export interface LanguageOption {
  code: string;
  gtValue: string; // '' = mostrar el texto base (Español) sin traducir
  nativeName: string;
  dir: 'ltr' | 'rtl';
}
export const LANGUAGES: LanguageOption[] = [{
  code: 'en',
  gtValue: 'en',
  nativeName: 'English',
  dir: 'ltr'
}, {
  code: 'es',
  gtValue: '',
  nativeName: 'Español',
  dir: 'ltr'
}, {
  code: 'fr',
  gtValue: 'fr',
  nativeName: 'Français',
  dir: 'ltr'
}, {
  code: 'zh-CN',
  gtValue: 'zh-CN',
  nativeName: '中文 (简体)',
  dir: 'ltr'
}, {
  code: 'zh-TW',
  gtValue: 'zh-TW',
  nativeName: '中文 (繁體)',
  dir: 'ltr'
}, {
  code: 'ko',
  gtValue: 'ko',
  nativeName: '한국어',
  dir: 'ltr'
}, {
  code: 'hy',
  gtValue: 'hy',
  nativeName: 'Հայերեն',
  dir: 'ltr'
}, {
  code: 'tl',
  gtValue: 'tl',
  nativeName: 'Filipino',
  dir: 'ltr'
}];
const STORAGE_KEY = 'preferredLang';
// Idiomas que se escriben de derecha a izquierda (ninguno en la lista, pero
// dejamos el mecanismo listo por si se agregan árabe/hebreo/persa/urdu).
const RTL_PREFIXES = ['ar', 'he', 'fa', 'ur'];
// --- DETECCIÓN DEL IDIOMA DEL DISPOSITIVO -------------------------------
// Orden: navigator.languages -> navigator.language -> inglés (respaldo)
function detectLanguage(): string {
  const candidates: string[] = [];
  if (typeof navigator !== 'undefined') {
    if (Array.isArray(navigator.languages)) candidates.push(...navigator.languages);
    if (navigator.language) candidates.push(navigator.language);
  }
  for (const raw of candidates) {
    if (!raw) continue;
    const lower = raw.toLowerCase();
    if (lower.startsWith('es')) return 'es';
    if (lower.startsWith('en')) return 'en';
    if (lower.startsWith('fr')) return 'fr';
    if (lower === 'zh-tw' || lower === 'zh-hant' || lower.startsWith('zh-hant')) return 'zh-TW';
    if (lower.startsWith('zh')) return 'zh-CN';
    if (lower.startsWith('ko')) return 'ko';
    if (lower.startsWith('hy')) return 'hy';
    if (lower.startsWith('tl') || lower.startsWith('fil')) return 'tl';
  }
  return 'en'; // respaldo
}
function resolveInitialLanguage(): string {
  // Prioridad 1: elección manual guardada
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && LANGUAGES.some(l => l.code === saved)) return saved;
  } catch {
    /* localStorage bloqueado */
  }
  // Prioridad 2: idioma del dispositivo. Prioridad 3: inglés (dentro de detect)
  return detectLanguage();
}
function getOption(code: string): LanguageOption {
  return LANGUAGES.find(l => l.code === code) || LANGUAGES[0];
}
// Escribe la cookie que lee Google Translate al inicializarse
function setGoogTransCookie(gtValue: string) {
  const value = gtValue ? `/es/${gtValue}` : `/es/es`;
  document.cookie = `googtrans=${value}; path=/`;
  try {
    document.cookie = `googtrans=${value}; path=/; domain=${window.location.hostname}`;
  } catch {
    /* dominio no accesible */
  }
}
// Aplica lang/dir al <html>
function applyHtmlAttributes(code: string) {
  const opt = getOption(code);
  const dir = RTL_PREFIXES.some(p => code.startsWith(p)) ? 'rtl' : opt.dir;
  document.documentElement.lang = code;
  document.documentElement.dir = dir;
}
// Cambia el idioma sin recargar usando el <select> oculto de Google Translate
function triggerInPlaceTranslate(gtValue: string) {
  const combo = document.querySelector('.goog-te-combo') as HTMLSelectElement | null;
  if (combo) {
    combo.value = gtValue;
    combo.dispatchEvent(new Event('change'));
    return true;
  }
  return false;
}
// --- CONTEXTO -----------------------------------------------------------
interface LanguageContextValue {
  lang: string;
  setLang: (code: string) => void;
  languages: LanguageOption[];
}
const LanguageContext = createContext<LanguageContextValue>({
  lang: 'en',
  setLang: () => {},
  languages: LANGUAGES
});
export function useLanguage() {
  return useContext(LanguageContext);
}
export function TranslationProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lang, setLangState] = useState<string>(() => resolveInitialLanguage());
  // Configurar la cookie y los atributos ANTES de cargar Google Translate,
  // para que la primera pintura ya salga en el idioma correcto (sin recarga).
  useEffect(() => {
    const initial = resolveInitialLanguage();
    setGoogTransCookie(getOption(initial).gtValue);
    applyHtmlAttributes(initial);
    setLangState(initial);
    // 1. Inicializar Google Translate (motor de traducción de todo el sitio)
    if (!(window as any).googleTranslateElementInit) {
      ;
      (window as any).googleTranslateElementInit = () => {
        new (window as any).google.translate.TranslateElement({
          pageLanguage: 'es',
          autoDisplay: false
        }, 'google_translate_element');
      };
      const script = document.createElement('script');
      script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      document.body.appendChild(script);
    }
    // 2. Mantener los TÍTULOS (h1, h2) en inglés — nunca se traducen.
    //    Hacemos una pasada inicial y luego una diferida, y desconectamos
    //    para no dejar un observador corriendo indefinidamente (mejor rendimiento móvil).
    const protectHeadlines = () => {
      document.querySelectorAll('h1, h2').forEach(h => {
        if (!h.classList.contains('notranslate')) {
          h.classList.add('notranslate');
          h.setAttribute('translate', 'no');
        }
      });
    };
    protectHeadlines();
    const observer = new MutationObserver(protectHeadlines);
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    // Desconectar tras la carga inicial: el DOM ya está estable.
    const stopTimer = window.setTimeout(() => observer.disconnect(), 4000);
    return () => {
      observer.disconnect();
      window.clearTimeout(stopTimer);
    };
  }, []);
  // Cambio manual de idioma: guarda en localStorage y aplica sin recargar.
  const setLang = useCallback((code: string) => {
    const opt = getOption(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      /* localStorage bloqueado */
    }
    setGoogTransCookie(opt.gtValue);
    applyHtmlAttributes(code);
    setLangState(code);
    // Intentar traducir en el sitio sin recargar; si el combo aún no existe
    // (Google Translate no ha cargado), la cookie ya quedó lista para el próximo render.
    const applied = triggerInPlaceTranslate(opt.gtValue);
    if (!applied) {
      // Reintento breve mientras el script de Google Translate termina de cargar.
      let attempts = 0;
      const retry = window.setInterval(() => {
        attempts += 1;
        if (triggerInPlaceTranslate(opt.gtValue) || attempts > 20) {
          window.clearInterval(retry);
        }
      }, 250);
    }
  }, []);
  return <LanguageContext.Provider value={{
    lang,
    setLang,
    languages: LANGUAGES
  }} data-id="element-413" data-mp="2yzo2p5yybiuf">
      {/* Contenedor oculto requerido por Google Translate */}
      <div id="google_translate_element" style={{
      display: 'none'
    }} data-id="element-414" data-mp="2k4hwahvy1i0k"></div>
      {children}
    </LanguageContext.Provider>;
}